import { useEffect, useRef, useState } from "react";

/* Boot ceremony shown while the auth session resolves on a hard load.
 *
 * Progress is theater, but honest theater: it creeps toward 90% for as long
 * as the session fetch is actually in flight, then rushes to 100% once the
 * caller flips `done`, settles for a beat, and fades out over the console
 * that rendered underneath. The log lines are fixed ceremony: they name
 * real boot stages but deliberately carry no fake numbers. */

const BOOT_LOG = [
	{ k: "authenticating session", v: "OK", at: 6 },
	{ k: "resolving workspace", v: "OK", at: 20 },
	{ k: "mounting agent registry", v: "READY", at: 36 },
	{ k: "sealing proxy handshake", v: "SECURE", at: 52 },
	{ k: "hydrating conversation index", v: "READY", at: 68 },
	{ k: "warming model routes", v: "WARM", at: 84 },
	{ k: "console ready", v: "↵", at: 100 },
] as const;

const SEGMENT_COUNT = 46;
const TICK_MS = 42;
/** Hold at 100% before fading, so "console ready" is readable. */
const SETTLE_MS = 420;
/** Must match the overlay's opacity transition duration. */
const FADE_MS = 550;

type BootScreenProps = {
	/** The session has resolved: rush to 100% and exit. */
	done: boolean;
	/** The fade-out finished; the caller should unmount the screen. */
	onExited: () => void;
};

export function BootScreen({ done, onExited }: BootScreenProps) {
	const [pct, setPct] = useState(0);
	const [exiting, setExiting] = useState(false);
	const doneRef = useRef(done);
	doneRef.current = done;
	const onExitedRef = useRef(onExited);
	onExitedRef.current = onExited;

	useEffect(() => {
		const reduceMotion =
			typeof window.matchMedia === "function" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		let settleTimer: number | undefined;
		let fadeTimer: number | undefined;

		const timer = window.setInterval(() => {
			setPct((current) => {
				if (current >= 100) {
					return current;
				}
				if (!doneRef.current) {
					// Two believable stalls on the way up, never past 90 while the
					// session fetch is still in flight.
					const stall =
						current > 34 && current < 42 ? 0.25 : current > 72 && current < 78 ? 0.3 : 1;
					return Math.min(90, current + (1.05 + Math.random() * 1.5) * stall);
				}
				if (reduceMotion) {
					window.clearInterval(timer);
					onExitedRef.current();
					return 100;
				}
				const next = Math.min(100, current + 6.5 + Math.random() * 2);
				if (next >= 100) {
					window.clearInterval(timer);
					settleTimer = window.setTimeout(() => {
						setExiting(true);
						fadeTimer = window.setTimeout(() => onExitedRef.current(), FADE_MS);
					}, SETTLE_MS);
				}
				return next;
			});
		}, TICK_MS);

		return () => {
			window.clearInterval(timer);
			window.clearTimeout(settleTimer);
			window.clearTimeout(fadeTimer);
		};
	}, []);

	const complete = pct >= 100;
	const filled = (pct / 100) * SEGMENT_COUNT;
	const visibleLog = BOOT_LOG.filter((line) => pct >= line.at);
	const phase = complete
		? "BOOT COMPLETE"
		: pct < 30
			? "AUTHENTICATING"
			: pct < 70
				? "MOUNTING SERVICES"
				: "HYDRATING CONSOLE";

	return (
		<output
			aria-label="Loading the ForgeDock console"
			className="fixed inset-0 z-50 block overflow-hidden bg-background text-foreground transition-opacity duration-[550ms] ease-out"
			style={{ opacity: exiting ? 0 : 1, pointerEvents: exiting ? "none" : "auto" }}
		>
			{/* Machined grid + furnace glow */}
			<div
				aria-hidden
				className="absolute inset-0 bg-[linear-gradient(var(--boot-grid)_1px,transparent_1px),linear-gradient(90deg,var(--boot-grid)_1px,transparent_1px)] bg-[size:56px_56px] [--boot-grid:color-mix(in_oklch,var(--color-border)_38%,transparent)]"
			/>
			<div
				aria-hidden
				className="absolute inset-0 bg-[radial-gradient(60%_55%_at_50%_46%,color-mix(in_oklch,var(--color-primary)_10%,transparent),transparent_70%)]"
			/>

			{/* Spec-sheet frame: status strip + corner brackets */}
			<div className="absolute inset-x-6 top-5 flex items-center gap-2.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.18em] text-muted-foreground/70">
				<span className="text-muted-foreground">Forge console</span>
				<span aria-hidden className="h-[11px] w-px bg-border" />
				<span>Self-hosted</span>
				<span className="flex-1" />
				<span>{complete ? "Ready" : "Booting…"}</span>
			</div>
			<div aria-hidden className="absolute left-3 top-3 h-4 w-4 border-l border-t border-border" />
			<div
				aria-hidden
				className="absolute bottom-3 right-3 h-4 w-4 border-b border-r border-border"
			/>

			<div className="absolute inset-0 flex flex-col items-center justify-center gap-8 p-6">
				{/* Mark + wordmark */}
				<div className="flex flex-col items-center gap-[18px]">
					<div className="flex h-[62px] w-[62px] select-none items-center justify-center rounded-xl bg-[linear-gradient(105deg,var(--color-primary)_42%,color-mix(in_oklch,var(--color-primary)_45%,white)_50%,var(--color-primary)_58%)] bg-[size:280%_100%] font-display text-3xl font-extrabold text-primary-foreground shadow-[0_8px_30px_color-mix(in_oklch,var(--color-primary)_22%,transparent)] animate-boot-sheen">
						F
					</div>
					<div className="flex flex-col items-center gap-[9px]">
						<div className="font-display font-wide text-[27px] font-extrabold uppercase leading-none tracking-[0.3em] [text-indent:0.3em]">
							Forgedock
						</div>
						<div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/80">
							{window.location.host}
						</div>
					</div>
				</div>

				{/* Segmented progress */}
				<div className="flex w-full max-w-[460px] flex-col gap-[11px]">
					<div className="flex items-baseline gap-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">
						<span>{phase}</span>
						<span className="flex-1" />
						<span className="text-[13px] tracking-[0.06em] text-primary">
							{String(Math.floor(pct)).padStart(3, "0")}%
						</span>
					</div>
					<div className="relative flex h-2 gap-[2px] overflow-hidden">
						{Array.from({ length: SEGMENT_COUNT }, (_, index) => {
							const on = index < Math.floor(filled);
							const edge = index === Math.floor(filled);
							return (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length, position-keyed meter
									key={index}
									className="flex-1 rounded-[1px] transition-[background-color,opacity] duration-150"
									style={{
										background: on
											? "var(--color-primary)"
											: edge
												? "color-mix(in oklch, var(--color-primary) 45%, transparent)"
												: "color-mix(in oklch, var(--color-border) 50%, var(--color-background))",
										opacity: on && index <= filled - 4 ? 0.82 : 1,
									}}
								/>
							);
						})}
						<div
							aria-hidden
							className="absolute inset-y-0 w-[26%] bg-[linear-gradient(90deg,transparent,color-mix(in_oklch,var(--color-primary)_22%,transparent),transparent)] animate-boot-sweep"
						/>
					</div>
				</div>

				{/* Boot log */}
				<div className="flex min-h-[132px] w-full max-w-[460px] flex-col gap-[5px]">
					{visibleLog.map((line, index) => {
						const isLast = index === visibleLog.length - 1;
						const active = isLast && !complete;
						return (
							<div
								key={line.k}
								className={`flex items-baseline gap-[9px] font-mono text-[11.5px] tracking-[0.02em] ${
									isLast ? "text-foreground/85" : "text-muted-foreground/80"
								}`}
							>
								<span className={`w-2.5 ${active ? "text-primary" : "text-success/70"}`}>
									{active ? "›" : "✓"}
								</span>
								<span>{line.k}</span>
								<span
									aria-hidden
									className="mx-0.5 mb-1 flex-1 border-b border-dotted border-border/70"
								/>
								<span className={isLast ? "text-primary" : "text-muted-foreground"}>{line.v}</span>
							</div>
						);
					})}
					<div
						className="flex h-[18px] items-center gap-2 font-mono text-[11.5px]"
						style={{ opacity: complete ? 0 : 1 }}
					>
						<span className="text-primary">›</span>
						<span className="h-[13px] w-[7px] bg-primary animate-boot-caret" />
					</div>
				</div>
			</div>
		</output>
	);
}
